<template>
  <FeatureGuard feature="solutions">
    <div class="viewsolution_section">
      <div v-if="article" id="solution-article-container-1" class="article-container">
        <div class="row">
          <div class="col-md-12">
            <h1>{{ article.title }}</h1>
            <div class="updated">
              {{ $t("solutions.article.updated") }}: {{ article.updatedAt | moment("D.M.YYYY") }}
            </div>
            <c-box
              class="article-body"
              v-chakra="{
                '& *': {
                  fontFamily: 'Avenir !important',
                },
              }"
            >
              <span v-html="article.descriptionHtml" />
            </c-box>
            <div v-if="(article.attachments || []).length > 0" class="article-attachments">
              <div
                v-for="attachment in article.attachments"
                class="article-attachment"
                :key="attachment.freshdeskId"
                @click="downloadAttachment(article, attachment.freshdeskId)"
              >
                <Icon name="download" size="xsmall" />
                <span class="attachment_name">
                  {{ attachment.name }}
                </span>
                <Icon
                  v-if="loadingUrlByAttachmentId[attachment.freshdeskId]"
                  variation="loading"
                  name="loading"
                  size="small"
                  class="loading"
                />
              </div>
            </div>
          </div>
          <!-- ToC may be displayed at some later point of time
          <div class="col-md-4">
            <div class="sidebar_toc_list">
              <h2>{{ parentTitle }}</h2>
              <div v-for="(item, index) in listItems" :key="index" class="list-item">
                <router-link :to="item.href">
                  <span :class="item.type" class="icon"></span>
                  <span>{{ item.title }}</span>
                </router-link>
              </div>
            </div>
          </div>
          -->
        </div>
      </div>
      <div v-else-if="loadingArticleFailed" class="note">
        {{ $t("solutions.loadingArticle.failed") }}<br />
        <a href="#" @click.stop.prevent="loadArticle">{{
          $t("solutions.loadingFailed.retryLink")
        }}</a>
      </div>
      <div v-else-if="loadingArticle" class="note">
        {{ $t("solutions.loadingArticle.processing") }}
      </div>
      <div v-else>({{ $t("solutions.loadingArticle.processing") }})</div>
    </div>
  </FeatureGuard>
</template>

<script lang="ts">
import Vue, { defineComponent, PropType } from "vue";
import Icon from "@/design-system/elements/Icon.vue";
import { mapState } from "vuex";
import {
  ISolutionArticle,
  ISolutionArticleSearchResult,
  ISolutionCategoryAndFolderStructure,
} from "@iloq/shared";
import Breadcrumb from "@/components/solutions/Breadcrumb.vue";
import { ISolutionsState } from "@/store/solutionsModule";
import FeatureGuard from "@/components/FeatureGuard.vue";
import { CBox } from "@chakra-ui/vue";
import { getCategoryName, getFolderName } from "@/util/SolutionUtil";
import { BreadcrumbItem } from "@/components/solutions/breadcrumb-item";

export default defineComponent({
  components: {
    Breadcrumb,
    FeatureGuard,
    Icon,
    CBox,
  },
  props: {
    solutionId: {
      type: String,
      required: true,
    },
    listItems: {
      type: Array as PropType<
        {
          title: string;
          href: string;
          type: string;
        }[]
      >,
      default: () => [],
    },
    parentTitle: String,
  },
  data() {
    return {
      article: null as ISolutionArticle | null,
      loadingArticle: false,
      loadingArticleFailed: false,
      path: [] as BreadcrumbItem[],
      loadingUrlByAttachmentId: {} as { [key: number]: boolean },
    };
  },
  computed: {
    ...mapState("solutions", {
      searchResult: (state: unknown) => (state as ISolutionsState).search.searchResult,
      lastPickedArticle: (state: unknown) => (state as ISolutionsState).quickSearch.lastPicked,
      structure: (state: unknown) => (state as ISolutionsState).structure,
    }),
  },
  methods: {
    pickArticleAndProcessHtml(articles: ISolutionArticle[]) {
      let article: ISolutionArticle | undefined;
      if (this.lastPickedArticle && this.solutionId === `${this.lastPickedArticle.freshdeskId}`) {
        article = this.lastPickedArticle;
      } else {
        article = articles.find(
          (a: ISolutionArticle): boolean => this.solutionId === `${a.freshdeskId}`,
        );
      }
      if (article) {
        const clone = { ...article };
        /*
        // Uncomment this to add fake cross reference links in the end of all articles
        clone.descriptionHtml = `${clone.descriptionHtml}
  <p><a href="https://support.iloq.com/sv-XX/support/solutions/articles/44002038405-asdfg?lang=en&x=y"
        class="original-classes-get-removed-but-we-do-not-care">LINK1</a></p>
  <p><a href="https://support.iloq.com/fi/support/solutions/articles/44002012546">LINK2</a></p>`;
         */
        this.processLinks(clone);
        this.processCrossReferences(clone);
        this.article = clone;

        this.path = [
          {
            name: this.$t("solutions.article.breadcrumb.root").toString(),
            href: "/support/solutions",
          },
          {
            name: this.getCategoryName(this.article),
            href: `/support/solutions/categories/${this.article.categoryId}`,
          },
          {
            name: this.getFolderName(this.article),
            href: `/support/solutions/categories/${this.article.categoryId}/${this.article.folderId}`,
          },
        ];

        // Elements need to exist in DOM while running this, that's why $nextTick()
        this.$nextTick(() => {
          this.setUpCrossReferenceHandlers();
        });
      } else {
        this.article = null;
      }
    },
    processLinks(article: ISolutionArticle) {
      article.descriptionHtml = article.descriptionHtml.replace(/<a /g, '<a target="_blank" ');
    },
    processCrossReferences(article: ISolutionArticle) {
      // Currently the data contains references to old url (iloq.freshdesk.com) too
      const oldMatches = article.descriptionHtml.match(
        /href="https:\/\/iloq\.freshdesk\.com\/[\w-]*\/support\/solutions\/articles\/\d\d*[^"]*"/g,
      ) as string[] | null;
      const newMatches = article.descriptionHtml.match(
        /href="https:\/\/support\.iloq\.com\/[\w-]*\/support\/solutions\/articles\/\d\d*[^"]*"/g,
      ) as string[] | null;
      [...(oldMatches || []), ...(newMatches || [])].forEach((match: string) => {
        // '"https://.../1234567-seo-title?possible-params=1"'   -->   1234567
        const articleId = parseInt(match.split("/").pop()!, 10);
        article.descriptionHtml = article.descriptionHtml.replace(
          match,
          `article-id="${articleId}" class="cross-reference" href="/support/solutions/${articleId}"`,
        );
      });
    },
    setUpCrossReferenceHandlers() {
      // Note: elements need to exist in DOM while running this
      // No better way was found to react to events from injected html
      const crossReferences = document
        .getElementById("solution-article-container-1")
        ?.getElementsByClassName("cross-reference");
      if (crossReferences) {
        Array.prototype.forEach.call(crossReferences, (crossReference: Element) => {
          crossReference.addEventListener("click", (event) => {
            event.preventDefault();
            let articleId: string | undefined = undefined;
            for (let i = 0; i < crossReference.attributes.length; i += 1) {
              if (crossReference.attributes[i].name === "article-id") {
                articleId = crossReference.attributes[i].value;
              }
            }
            if (articleId) {
              this.$router.push({ path: `/support/solutions/${articleId}` });
            }
          });
        });
      }
    },
    loadArticle() {
      this.$store
        .dispatch("solutions/searchArticlesQuick", { term: this.solutionId, top: 50 })
        .then((results: ISolutionArticleSearchResult) => {
          this.pickArticleAndProcessHtml(results.value);
          this.loadingArticle = false;
          if (!this.article) {
            this.loadingArticleFailed = true;
          }
        })
        .catch(() => {
          this.loadingArticle = false;
          this.loadingArticleFailed = true;
        });
    },
    loadData() {
      this.loadingArticleFailed = false;
      this.loadingArticle = true;

      if (this.structureMissing()) {
        this.$store
          .dispatch("solutions/fetchStructure")
          .then(() => {
            if (this.article) {
              this.loadingArticle = false;
            } else {
              this.loadArticle();
            }
          })
          .catch(() => {
            this.loadingArticle = false;
            this.loadingArticleFailed = true;
          });
      } else {
        this.loadArticle();
      }
    },
    structureMissing() {
      return !this.structure || this.structure.categories.length === 0;
    },
    onNavigate(target: BreadcrumbItem) {
      this.$router.push({ path: target.href });
    },
    initData() {
      window.scrollTo(0, 0);

      if (!this.structureMissing() && this.searchResult) {
        this.pickArticleAndProcessHtml(this.searchResult.value);
      }
      // Module data is not initialized yet (landed directly to this page or problems earlier)
      if (!this.article) {
        this.loadData();
      }
    },
    getCategoryName(article: ISolutionArticle): string {
      return getCategoryName(this.structure, article.categoryId, article.categoryName);
    },
    getFolderName(article: ISolutionArticle): string {
      return getFolderName(this.structure, article.folderId, article.folderName);
    },
    async downloadAttachment(article: ISolutionArticle, attachmentId: number) {
      if (!this.loadingUrlByAttachmentId[attachmentId]) {
        Vue.set(this.loadingUrlByAttachmentId, attachmentId, true);
        try {
          const url = await this.$store.dispatch("solutions/fetchSolutionAttachmentURL", {
            article,
            attachmentId,
          });
          if (url) {
            window.open(url, "_blank");
          }
        } finally {
          Vue.set(this.loadingUrlByAttachmentId, attachmentId, undefined);
        }
      }
    },
  },
  watch: {
    solutionId: "initData",
  },
  mounted() {
    this.initData();
  },
});
</script>

<style lang="scss">
@import "../design-system/assets/tokens/tokens";
.article-body {
  ul {
    display: block;
    list-style-type: disc;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    padding-inline-start: 40px;
  }

  ol {
    display: block;
    list-style-type: decimal;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    padding-inline-start: 40px;
  }

  h1 {
    display: block;
    font-size: 2em;
    margin-block-start: 0.67em;
    margin-block-end: 0.67em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }

  h2 {
    display: block;
    font-size: 1.5em;
    margin-block-start: 0.83em;
    margin-block-end: 0.83em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }

  h3 {
    display: block;
    font-size: 1.17em;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }

  h4 {
    display: block;
    margin-block-start: 1.33em;
    margin-block-end: 1.33em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }

  h5 {
    display: block;
    font-size: 0.83em;
    margin-block-start: 1.67em;
    margin-block-end: 1.67em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }

  h6 {
    display: block;
    font-size: 0.67em;
    margin-block-start: 2.33em;
    margin-block-end: 2.33em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }
}
.viewsolution_section {
  .note {
    padding-top: 40px;
    font-size: 18px;
  }
  .article-container {
    font-size: 12px;

    .breadcrumb-container {
      margin-bottom: 18px;
    }

    h1 {
      font-size: 32px;
      line-height: 40px;
      font-weight: $weight-medium;
      margin-bottom: 10px;
    }
    .updated {
      color: $color-iloq-gray-dark;
      margin-bottom: 24px;
    }
    p {
      margin: 0 0 18px;
    }

    a[target="_blank"]:not(.cross-reference)::after {
      content: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAQElEQVR42qXKwQkAIAxDUUdxtO6/RBQkQZvSi8I/pL4BoGw/XPkh4XigPmsUgh0626AjRsgxHTkUThsG2T/sIlzdTsp52kSS1wAAAABJRU5ErkJggg==);
      margin: 0 3px 0 5px;
    }

    * {
      max-width: 100%;
    }

    .article-attachments {
      margin: $space-m 0;
      color: $color-iloq-blue-dark;
      font-size: $size-iloq-s;
      .article-attachment {
        margin: 4px 0;
        &:hover {
          cursor: pointer;
        }
        .icon {
          margin: 0 $space-xxxs;
          padding: 0;
          color: $color-iloq-blue-light;
          svg {
            fill: $color-iloq-blue-light !important;
          }
          &.loading {
            height: 16px;
          }
        }
      }
    }
  }

  .sidebar_toc_list {
    background: #f7f7f7;
    padding: 24px;
    box-shadow: 0 1px 4px 0 darken($color-iloq-gray-light, 20%);
  }

  .sidebar_toc_list .list-item {
    padding: 12px 0 10px;
    a {
      text-decoration: none;
      font-size: 15px;
      font-weight: $weight-bold;
      .icon {
        display: inline-block;
        background: url("/img/components/icons/folder.svg") no-repeat scroll center center;
        background-size: 16px 16px;
        margin: 0 10px -3px 0;
        padding: 0;
        width: 16px;
        height: 16px;
        border: none;
        &.article {
          background: url("/img/components/icons/article.svg") no-repeat scroll center center;
        }
        &.video {
          background: url("/img/components/icons/video.svg") no-repeat scroll center center;
        }
      }
    }
    border-bottom: 1px solid $color-iloq-gray-light;

    .router-link-active {
      color: $color_iloq_orange;
      // #ED8B00
    }
  }
  :last-child {
    border-bottom: none;
  }
}
</style>
